export enum Country {
  PERU = 'peru',
  CHILE = 'chile',
  MEXICO = 'mexico',
  ECUADOR = 'ecuador',
  COLOMBIA = 'colombia',
}

type TaxValidator = (value: string) => boolean;
type CountryErrorMessages = Record<Country, string>;
type TaxLabels = Record<Country, string>;
type CountryCache = Map<string, Country | null>;

const countryCache: CountryCache = new Map();

const normalizeString = (str: string): string =>
  str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const stringToCountry = (country: string): Country | null => {
  if (!country) return null;

  const normalized = normalizeString(country);
  const cached = countryCache.get(normalized);
  if (cached !== undefined) return cached;

  let result: Country | null = null;

  switch (normalized) {
    case 'peru':
      result = Country.PERU;
      break;
    case 'chile':
      result = Country.CHILE;
      break;
    case 'mexico':
      result = Country.MEXICO;
      break;
    case 'ecuador':
      result = Country.ECUADOR;
      break;
    case 'colombia':
      result = Country.COLOMBIA;
      break;
    default:
      result = null;
  }

  countryCache.set(normalized, result);
  return result;
};

const TAX_ID_LENGTHS = {
  PERU_RUC: 11,
  PERU_PREFIX: ['10', '20'],
  COLOMBIA_NIT: { MIN: 10, MAX: 11 },
  ECUADOR_RUC: 13,
  CHILE_RUT: { MIN: 7, MAX: 8 },
  MEXICO_RFC: {
    DIGITS: 6,
    MORAL_PREFIX: 3,
    FISICA_PREFIX: 4,
    HOMOCLAVE_LETTERS: 2,
    VERIFICATION_DIGITS: 1,
  },
} as const;

const TAX_LABELS: TaxLabels = {
  [Country.PERU]: 'RUC',
  [Country.CHILE]: 'RUT',
  [Country.MEXICO]: 'RFC',
  [Country.ECUADOR]: 'RUC',
  [Country.COLOMBIA]: 'NIT',
};

export const getTaxFieldLabel = (country: string): string => {
  const normalizedCountry = stringToCountry(country);
  return normalizedCountry ? TAX_LABELS[normalizedCountry] : 'Tax Number';
};

const validatePeruRuc = (ruc: string): boolean => {
  const peruRegex = new RegExp(
    `^(${TAX_ID_LENGTHS.PERU_PREFIX.join('|')})\\d{${TAX_ID_LENGTHS.PERU_RUC - 2}}$`
  );
  return peruRegex.test(ruc);
};

const validateColombiaNit = (nit: string): boolean => {
  const colombiaRegex = new RegExp(
    `^\\d{${TAX_ID_LENGTHS.COLOMBIA_NIT.MIN},${TAX_ID_LENGTHS.COLOMBIA_NIT.MAX}}$`
  );
  return colombiaRegex.test(nit);
};

const validateChileRut = (rut: string): boolean => {
  if (!rut) return true;
  const rutRegex = new RegExp(
    `^(\\d{${TAX_ID_LENGTHS.CHILE_RUT.MIN},${TAX_ID_LENGTHS.CHILE_RUT.MAX}})([0-9K])$`,
    'i'
  );
  return rutRegex.test(rut);
};

const validateEcuadorRuc = (ruc: string): boolean => {
  const ecuadorRegex = new RegExp(`^\\d{${TAX_ID_LENGTHS.ECUADOR_RUC}}$`);
  return ecuadorRegex.test(ruc);
};

const validateMexicoRfc = (rfc: string): boolean => {
  if (!rfc) return true;
  rfc = rfc.toUpperCase();

  const { MORAL_PREFIX, FISICA_PREFIX, DIGITS, HOMOCLAVE_LETTERS, VERIFICATION_DIGITS } =
    TAX_ID_LENGTHS.MEXICO_RFC;
  const personaMoralRegex = new RegExp(
    `^[A-Z]{${MORAL_PREFIX}}\\d{${DIGITS}}[A-Z]{${HOMOCLAVE_LETTERS}}\\d{${VERIFICATION_DIGITS}}$`
  );
  const personaFisicaRegex = new RegExp(
    `^[A-Z]{${FISICA_PREFIX}}\\d{${DIGITS}}[A-Z]{${HOMOCLAVE_LETTERS}}\\d{${VERIFICATION_DIGITS}}$`
  );

  return personaMoralRegex.test(rfc) || personaFisicaRegex.test(rfc);
};

const TAX_VALIDATORS: Record<Country, TaxValidator> = {
  [Country.PERU]: validatePeruRuc,
  [Country.CHILE]: validateChileRut,
  [Country.MEXICO]: validateMexicoRfc,
  [Country.ECUADOR]: validateEcuadorRuc,
  [Country.COLOMBIA]: validateColombiaNit,
};

export const validateTaxId = (country: string, value: string): boolean => {
  const normalizedCountry = stringToCountry(country);
  if (!normalizedCountry) {
    return validateChileRut(value);
  }

  return TAX_VALIDATORS[normalizedCountry](value);
};

const ERROR_MESSAGES: CountryErrorMessages = {
  [Country.PERU]: 'RUC must be 11 digits and start with 10 or 20',
  [Country.COLOMBIA]: 'NIT must be 10 or 11 digits',
  [Country.CHILE]: 'RUT must be 8-9 characters, ending only with a number or K',
  [Country.ECUADOR]: 'RUC must be 13 digits',
  [Country.MEXICO]:
    'RFC must be: 3-4 letters + 6 digits + 2 letters + 1 digit (Example: ABCD123456GR5)',
};

export const getTaxIdErrorMessage = (country: string): string => {
  const normalizedCountry = stringToCountry(country);
  return normalizedCountry
    ? ERROR_MESSAGES[normalizedCountry]
    : 'Invalid tax ID format for the selected country';
};
